import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ClipboardList } from 'lucide-react';
import { useEndpoints } from '../../hooks/useEndpoints';
import DateNavigator from './DateNavigator';
import styles from './AthleteSubjectiveReadiness.module.scss';

interface AthleteSubjectiveReadinessProps {
  athleteId: string;
}

interface HooperEntry {
  _id: string;
  date: string;
  fatigue: number;
  sleepQuality: number;
  muscleSoreness: number;
  stressLevel: number;
  mood?: number;
  average: number;
  skipped: boolean;
  sleepHours?: number;
  readinessToTrain?: number;
  notes?: string;
}

const PARAM_CONFIG = [
  { key: 'sleepQuality' as const, label: 'Sonno', color: '#5C6BC0' },
  { key: 'stressLevel' as const, label: 'Stress', color: '#EF5350' },
  { key: 'fatigue' as const, label: 'Fatica', color: '#FFA726' },
  { key: 'muscleSoreness' as const, label: 'DOMS', color: '#AB47BC' },
  { key: 'sleepHours' as const, label: 'Ore sonno', color: '#00897B' },
];

type TimeframeKey = '7d' | '14d' | '30d' | '90d';
const TIMEFRAME_LABELS: Record<TimeframeKey, string> = {
  '7d': '7 giorni',
  '14d': '14 giorni',
  '30d': '30 giorni',
  '90d': '90 giorni',
};

function getValueColor(value: number): string {
  if (value >= 4) return '#27ae60';
  if (value >= 3) return '#FFD15A';
  if (value >= 2) return '#FF9F43';
  return '#f57c73';
}

function getValueBg(value: number): string {
  if (value >= 4) return '#eff9f4';
  if (value >= 3) return '#fffbef';
  if (value >= 2) return '#fff5eb';
  return '#fbf2f1';
}

export default function AthleteSubjectiveReadiness({ athleteId }: AthleteSubjectiveReadinessProps) {
  const endpoints = useEndpoints();
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [timeframe, setTimeframe] = useState<TimeframeKey>('30d');
  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    () => new Set(PARAM_CONFIG.map(p => p.key))
  );
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const toggleLine = (key: string) => {
    setVisibleLines(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Date range for the trend chart based on timeframe
  const trendRange = useMemo(() => {
    const days = parseInt(timeframe);
    const end = dayjs();
    const start = end.subtract(days, 'day');
    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    };
  }, [timeframe]);

  // Single query with startDate/endDate based on timeframe
  const readinessQuery = useQuery({
    queryKey: ['subjective-readiness', athleteId, trendRange.startDate, trendRange.endDate],
    queryFn: async () => {
      const { data } = await endpoints.getSubjectReadiness({
        userId: athleteId,
        startDate: trendRange.startDate,
        endDate: trendRange.endDate,
      });
      const raw = Array.isArray(data) ? data : [];
      const user = raw.find((u: any) => u._id === athleteId);
      if (!user?.hooperQuestionnaire) return [] as HooperEntry[];
      const questionnaires = Array.isArray(user.hooperQuestionnaire) ? user.hooperQuestionnaire : [user.hooperQuestionnaire];
      return questionnaires as HooperEntry[];
    },
    staleTime: 2 * 60 * 1000,
  });

  // All entries sorted desc (for table + summary cards)
  const allEntries = useMemo(() => {
    const entries = readinessQuery.data ?? [];
    return [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [readinessQuery.data]);

  // Entry for the selected date
  const selectedEntry = useMemo(() => {
    return allEntries.find(e => !e.skipped && e.date.startsWith(selectedDate)) ?? null;
  }, [allEntries, selectedDate]);

  // Chart data sorted chronologically (no skipped)
  const trendEntries = useMemo(() => {
    return [...allEntries].filter(e => !e.skipped).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [allEntries]);

  const chartData = useMemo(() => {
    const activeKeys = PARAM_CONFIG.filter(p => visibleLines.has(p.key)).map(p => p.key);
    return trendEntries.map(e => {
      const avg = activeKeys.length > 0
        ? activeKeys.reduce((sum, k) => sum + (e[k as keyof HooperEntry] as number), 0) / activeKeys.length
        : 0;
      return {
        date: new Date(e.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
        sleepQuality: e.sleepQuality,
        stressLevel: e.stressLevel,
        fatigue: e.fatigue,
        muscleSoreness: e.muscleSoreness,
        sleepHours: e.sleepHours,
        avg: parseFloat(avg.toFixed(2)),
      };
    });
  }, [trendEntries, visibleLines]);

  // Pagination for table
  const totalPages = Math.max(1, Math.ceil(allEntries.length / pageSize));
  const pagedEntries = allEntries.slice((page - 1) * pageSize, page * pageSize);

  if (readinessQuery.isLoading) {
    return (
      <div className={styles.loadingState}>
        <LeopartLoader />
      </div>
    );
  }

  if (allEntries.length === 0) {
    return (
      <div className={styles.emptyState}>
        <ClipboardList size={40} strokeWidth={1.2} style={{ color: 'var(--c-text-3)' }} />
        <p className={styles.emptyTitle}>Nessun questionario compilato</p>
        <p className={styles.emptySubtitle}>I dati appariranno quando l'atleta compilerà il questionario Hooper</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Date navigator + Summary cards */}
      <DateNavigator selectedDate={selectedDate} onDateChange={setSelectedDate} />

      {selectedEntry ? (
        <div className={styles.summaryGrid}>
          {/* Average card (dark) */}
          <div className={`${styles.summaryCard} ${styles.summaryAvgCard}`}>
            <p className={styles.summaryLabel}>Media</p>
            <div className={styles.summaryValueRow}>
              <p className={styles.summaryValue}>{selectedEntry.average.toFixed(1)}</p>
              <p className={styles.summaryScale}>/ 5</p>
            </div>
            <div className={styles.summaryBar}>
              <div
                className={styles.summaryBarFill}
                style={{ width: `${(selectedEntry.average / 5) * 100}%`, background: '#FFD15A' }}
              />
            </div>
          </div>

          {PARAM_CONFIG.map(({ key, label, color }) => {
            const val = selectedEntry[key];
            const isSleepHours = key === 'sleepHours';
            const scale = isSleepHours ? 12 : 5;
            const displayVal = val ?? '-';
            const barWidth = val != null ? Math.min((val / scale) * 100, 100) : 0;
            return (
              <div key={key} className={styles.summaryCard}>
                <p className={styles.summaryLabel}>{label}</p>
                <div className={styles.summaryValueRow}>
                  <p className={styles.summaryValue}>{displayVal}</p>
                  <p className={styles.summaryScale}>/ {scale}</p>
                </div>
                <div className={styles.summaryBar}>
                  <div
                    className={styles.summaryBarFill}
                    style={{ width: `${barWidth}%`, background: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.emptyState} style={{ padding: '16px' }}>
          <p className={styles.emptySubtitle}>Nessun questionario compilato per questo giorno</p>
        </div>
      )}

      {/* Timeframe filter (shared between chart and table) */}
      <div className={styles.timeframeBar}>
        <p className={styles.timeframeLabel}>Periodo</p>
        <div className={styles.timeframePills}>
          {(Object.keys(TIMEFRAME_LABELS) as TimeframeKey[]).map(tf => (
            <button
              key={tf}
              className={`${styles.timeframePill} ${timeframe === tf ? styles.timeframePillActive : ''}`}
              onClick={() => { setTimeframe(tf); setPage(1); }}
            >
              {TIMEFRAME_LABELS[tf]}
            </button>
          ))}
        </div>
      </div>

      {/* Trend chart */}
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <div>
            <p className={styles.chartTitle}>Trend questionario soggettivo</p>
            <p className={styles.chartSubtitle}>
              {trendEntries.length} compilazion{trendEntries.length === 1 ? 'e' : 'i'} negli ultimi {TIMEFRAME_LABELS[timeframe]}
            </p>
          </div>
        </div>

        {/* Toggleable legend */}
        <div className={styles.legendRow}>
          {PARAM_CONFIG.map(({ key, label, color }) => {
            const active = visibleLines.has(key);
            return (
              <button
                key={key}
                className={`${styles.legendChip} ${active ? styles.legendChipActive : ''}`}
                style={active ? { borderColor: color, background: `${color}14` } : undefined}
                onClick={() => toggleLine(key)}
              >
                <div className={styles.legendDot} style={{ background: active ? color : '#ccc' }} />
                {label}
              </button>
            );
          })}
        </div>

        {chartData.length > 1 ? (
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#efefef" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
                  tickLine={false}
                  axisLine={{ stroke: '#efefef' }}
                />
                <YAxis
                  domain={[1, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                  tick={{ fill: '#999', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <ReferenceLine y={3} stroke="#d4d4d4" strokeDasharray="6 4" />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const avgEntry = payload.find((e: any) => e.dataKey === 'avg');
                    return (
                      <div className={styles.customTooltip}>
                        <p className={styles.tooltipDate}>{label}</p>
                        <div className={styles.tooltipGrid}>
                          {payload.map((entry: any) => {
                            const cfg = PARAM_CONFIG.find(p => p.key === entry.dataKey);
                            if (!cfg) return null;
                            return (
                              <div key={entry.dataKey} className={styles.tooltipRow}>
                                <div className={styles.tooltipDot} style={{ background: cfg.color }} />
                                <span className={styles.tooltipLabel}>{cfg.label}</span>
                                <span className={styles.tooltipValue}>{entry.value}</span>
                              </div>
                            );
                          })}
                        </div>
                        {avgEntry && (
                          <div className={styles.tooltipAvgRow}>
                            <span className={styles.tooltipLabel}>Media</span>
                            <span className={styles.tooltipValue}>{avgEntry.value}</span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                {PARAM_CONFIG.map(({ key, color }) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={color}
                    strokeWidth={visibleLines.has(key) ? 2.5 : 0}
                    dot={visibleLines.has(key) ? { fill: '#fff', stroke: color, strokeWidth: 2, r: 4 } : false}
                    activeDot={visibleLines.has(key) ? { fill: color, stroke: '#fff', strokeWidth: 2, r: 6 } : false}
                    connectNulls
                    hide={!visibleLines.has(key)}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="avg"
                  stroke="#00212c"
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  dot={false}
                  activeDot={{ fill: '#00212c', stroke: '#fff', strokeWidth: 2, r: 5 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className={styles.emptyState} style={{ padding: '32px' }}>
            <p className={styles.emptySubtitle}>Dati insufficienti per il grafico nel periodo selezionato</p>
          </div>
        )}
      </div>

      {/* History table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <p className={styles.tableTitle}>Storico compilazioni</p>
        </div>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th><p className={styles.thLabel}>Data</p></th>
              {PARAM_CONFIG.map(({ key, label }) => (
                <th key={key}><p className={styles.thLabel}>{label}</p></th>
              ))}
              <th><p className={styles.thLabel}>Media</p></th>
            </tr>
          </thead>
          <tbody>
            {pagedEntries.map((entry) => (
              <tr key={entry._id} className={styles.tableRow}>
                <td className={styles.tdDate}>
                  {new Date(entry.date).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
                {entry.skipped ? (
                  <td colSpan={6} className={styles.tdCenter}>
                    <span className={styles.skipBadge}>Saltato</span>
                  </td>
                ) : (
                  <>
                    {PARAM_CONFIG.map(({ key }) => {
                      const val = entry[key];
                      if (val == null) {
                        return <td key={key} className={styles.tdCenter}>-</td>;
                      }
                      return (
                        <td key={key} className={styles.tdCenter}>
                          <span
                            className={styles.valueBadge}
                            style={{
                              background: getValueBg(val),
                              color: getValueColor(val),
                            }}
                          >
                            {val}
                          </span>
                        </td>
                      );
                    })}
                    <td className={styles.tdAvg}>
                      <span
                        className={styles.valueBadge}
                        style={{
                          background: getValueBg(entry.average),
                          color: getValueColor(entry.average),
                        }}
                      >
                        {entry.average.toFixed(1)}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className={styles.paginationRow}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Precedente
            </button>
            <span className={styles.pageInfo}>{page} / {totalPages}</span>
            <button
              className={styles.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Successiva
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
